import { Component, inject } from '@angular/core';

import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

/**
 * Configuration for {@link ConfirmDeleteDialogComponent}. Every field is
 * optional so a caller can `dialog.open(ConfirmDeleteDialogComponent)` with no
 * data and get sensible "delete this item" defaults. Pass `isDeleting` (and
 * optionally re-assign `componentInstance.data`) to drive the in-progress spinner.
 */
export interface ConfirmDeleteDialogData {
  header?: string;
  content?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  busyLabel?: string;
  isDeleting?: boolean;
}

/**
 * Single parameterized confirm-delete dialog. Replaces the per-entity
 * region/club/customer/event delete dialogs, which were identical except for
 * their copy. Closes with `true` on confirm, `false` on cancel.
 */
@Component({
  selector: 'bodyanalytics-confirm-delete-dialog',
  imports: [MatDialogModule],
  templateUrl: './confirm-delete-dialog.component.html',
  styleUrl: './confirm-delete-dialog.component.scss',
})
export class ConfirmDeleteDialogComponent {
  dialogRef = inject<MatDialogRef<ConfirmDeleteDialogComponent, boolean>>(MatDialogRef);
  data = inject<ConfirmDeleteDialogData | null>(MAT_DIALOG_DATA);

  get header(): string {
    return this.data?.header ?? 'Confirm Deletion';
  }

  get content(): string {
    return this.data?.content ?? 'Are you sure you want to delete this item? This action cannot be undone.';
  }

  get confirmLabel(): string {
    return this.data?.confirmLabel ?? 'Delete';
  }

  get cancelLabel(): string {
    return this.data?.cancelLabel ?? 'Cancel';
  }

  get busyLabel(): string {
    return this.data?.busyLabel ?? 'Deleting...';
  }

  get isDeleting(): boolean {
    return this.data?.isDeleting ?? false;
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  onConfirm(): void {
    this.dialogRef.close(true);
  }
}
